import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useForm } from 'react-hook-form';
import { ContractorPortalUser, ContractorPortalUserFormData } from '@/hooks/useContractorPortalUsers';
import { useEffect } from 'react';

interface EditPortalUserDialogProps {
  user: ContractorPortalUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (id: string, data: Partial<ContractorPortalUserFormData>) => Promise<void>;
}

export function EditPortalUserDialog({ user, open, onOpenChange, onSubmit }: EditPortalUserDialogProps) {
  const { register, handleSubmit, formState: { isSubmitting }, setValue, watch, reset } = useForm<ContractorPortalUserFormData>();

  useEffect(() => {
    if (user) {
      reset({
        email: user.email,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        title: user.title || '',
        is_primary_contact: user.is_primary_contact,
        can_upload_documents: user.can_upload_documents,
        can_view_prequalification_status: user.can_view_prequalification_status,
      });
    }
  }, [user, reset]);

  const handleFormSubmit = async (data: ContractorPortalUserFormData) => {
    if (!user) return;
    await onSubmit(user.id, data);
    onOpenChange(false);
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Portal User</DialogTitle>
          <DialogDescription>
            Update contractor portal user details and permissions.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                required
                {...register('email')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                {...register('title')}
                placeholder="e.g., Project Manager"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                {...register('first_name')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                {...register('last_name')}
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                {...register('phone')}
              />
            </div>
          </div>

          <div className="space-y-3 pt-2 border-t">
            <Label>Permissions</Label>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_primary_contact"
                checked={watch('is_primary_contact')}
                onCheckedChange={(checked) => setValue('is_primary_contact', checked as boolean)}
              />
              <Label htmlFor="is_primary_contact" className="font-normal cursor-pointer">
                Primary Contact
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="can_upload_documents"
                checked={watch('can_upload_documents')}
                onCheckedChange={(checked) => setValue('can_upload_documents', checked as boolean)}
              />
              <Label htmlFor="can_upload_documents" className="font-normal cursor-pointer">
                Can Upload Documents
              </Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="can_view_prequalification_status"
                checked={watch('can_view_prequalification_status')}
                onCheckedChange={(checked) => setValue('can_view_prequalification_status', checked as boolean)}
              />
              <Label htmlFor="can_view_prequalification_status" className="font-normal cursor-pointer">
                Can View Prequalification Status
              </Label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
